import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Snackbar, Alert, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloseIcon from '@mui/icons-material/Close';

import { PageHeader, FilterChips, ConfirmDialog, SkeletonCards, EmptyState, ErrorState, PermissionState, FeatureUsageMeter } from '../ui';
import HelpMark from '../help/HelpMark';
import InvoiceRow from './InvoiceRow';
import useInvoicesPage from './useInvoicesPage';
import InvoiceCreateDialog from './InvoiceCreateDialog';
import InvoiceEmailDialog from './InvoiceEmailDialog';
import InvoiceDetailDialog from './InvoiceDetailDialog';
import InvoiceContactsDialog from './InvoiceContactsDialog';
import { radius } from '../../themes/smartinventoryTheme';
import { formatCurrency } from '../../utils/currencyFormatter';

const Invoices = () => {
  const { i18n } = useTranslation();
  const p = useInvoicesPage();
  const showList = p.effectiveStatus === 'ready' && p.effectiveRows.length > 0;

  const statusOptions = [
    { value: 'all', label: p.t('All Statuses') },
    { value: 'draft', label: p.t('Draft') },
    { value: 'issued', label: p.t('Issued') },
    { value: 'paid', label: p.t('Paid') },
    { value: 'void', label: p.t('Void') },
    { value: 'processing_issue', label: p.t('Issuing…') },
  ];

  return (
    <Box>
      <PageHeader
        title={p.t('Invoices')}
        helpKey="invoices"
        subtitle={p.t('{{count}} invoices', { count: p.total, defaultValue: `${p.total} invoices` })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search by Order #, Product Name', { defaultValue: 'Search invoice # or customer' }) }}
        actions={[
          { label: p.t('Refresh'), icon: RefreshIcon, variant: 'outlined', onClick: p.handleRefresh, tourId: 'invoices-refresh' },
          { label: p.t('Manage Custom Invoice Recipients'), icon: PeopleIcon, variant: 'outlined', onClick: () => p.setContactsDialogOpen(true), tourId: 'invoices-contacts' },
          {
            label: p.t('Create Invoice'), icon: AddIcon, variant: 'contained', onClick: p.handleOpenCreate,
            disabled: p.planLimit?.atLimit, disabledReason: p.planLimit?.tooltip, tourId: 'invoices-create',
          },
        ]}
        meter={<FeatureUsageMeter feature="invoices.issued.monthly" label={p.t('Invoices issued this month', { defaultValue: 'Invoices issued this month' })} />}
      />

      <Box data-tour="invoices-filters" sx={{ mb: 1.5 }}>
        <FilterChips options={statusOptions} value={p.statusFilter} onChange={p.setStatusFilter} filtersLabel={p.t('Filters')} />
      </Box>

      <Box sx={{ border: showList ? '1px solid' : 'none', borderColor: 'divider', borderRadius: `${radius.xl}px`, overflow: 'hidden', bgcolor: showList ? 'background.paper' : 'transparent' }}>
        {p.effectiveStatus === 'loading' && <SkeletonCards />}

        {p.effectiveStatus === 'error' && (
          <ErrorState
            scope="page"
            title={p.t("Couldn't load invoices", { defaultValue: "Couldn't load invoices" })}
            description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
            request={p.errorInfo?.request}
            retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
            onRetry={p.fetchInvoices}
          />
        )}

        {p.effectiveStatus === 'permission' && (
          <PermissionState
            title={p.t("You don't have access to Invoices", { defaultValue: "You don't have access to Invoices" })}
            description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
          />
        )}

        {p.effectiveStatus === 'ready' && p.effectiveRows.length === 0 && (
          p.isNoMatch ? (
            <EmptyState
              kind="no-match"
              title={p.t('No products match "{{query}}"', { query: p.search, defaultValue: 'No invoices match your filters' })}
              description={p.t('Try a different search or clear the status filter.', { defaultValue: 'Try a different search or clear the status filter.' })}
              actions={[{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); p.setStatusFilter('all'); } }]}
            />
          ) : (
            <EmptyState
              kind="none"
              icon={ReceiptIcon}
              title={p.t('No invoices found.')}
              description={p.t('Create an invoice to start billing your customers.', { defaultValue: 'Create an invoice to start billing your customers.' })}
              actions={[{
                label: p.t('Create Invoice'), variant: 'contained', onClick: p.handleOpenCreate,
                disabled: p.planLimit?.atLimit, disabledReason: p.planLimit?.tooltip,
              }]}
            />
          )
        )}

        {showList && (
          <Box data-tour="invoices-list-header" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {p.t('Invoice · status · email', { defaultValue: 'Invoice · status · email' })}
            </Typography>
            <HelpMark id="invoices.list-header" variant="outlined" />
          </Box>
        )}

        {showList && p.effectiveRows.map((invoice, index) => {
          const needsManualReview = Boolean(p.manualReviewInvoices[invoice._id]) || (p.processingIssueChecks[invoice._id] || 0) >= 3;
          return (
            <InvoiceRow
              key={invoice._id}
              t={p.t}
              i18n={i18n}
              invoice={invoice}
              needsManualReview={needsManualReview}
              tourAnchor={index === 0}
              onView={p.handleOpenDetail}
              onEdit={p.handleOpenEdit}
              onIssue={(inv) => p.handleOpenConfirm('issue', inv)}
              onDelete={(inv) => p.handleOpenConfirm('delete', inv)}
              onMarkPaid={(inv) => p.handleOpenConfirm('paid', inv)}
              onVoid={(inv) => p.handleOpenConfirm('void', inv)}
              onSendEmail={p.handleOpenEmail}
              onRemind={p.handleRemind}
              onDownloadPdf={p.handleDownloadPdf}
              onPrintLabel={p.handlePrintShippingLabel}
              onDownloadLabel={p.handleDownloadShippingLabel}
              onRefresh={p.handleRefreshInvoice}
            />
          );
        })}
      </Box>

      {p.remaining > 0 && showList && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={p.loadMore} disabled={p.loadingMore} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('Load {{count}} more · {{remaining}} remaining', {
              count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
            })}
          </Button>
        </Box>
      )}

      <ConfirmDialog
        open={p.confirmDialog.open}
        onClose={p.handleCloseConfirm}
        tone={p.confirmDialog.type === 'issue' || p.confirmDialog.type === 'paid' ? 'primary' : 'danger'}
        title={p.t('Confirm Action')}
        description={p.confirmDialogText()}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Confirm')}
        onConfirm={p.handleConfirmAction}
        loading={p.actionPending}
      />

      <InvoiceCreateDialog
        open={p.createDialogOpen}
        onClose={p.handleCloseCreateDialog}
        onSaved={p.handleSaved}
        invoiceClient={p.invoiceClient}
        users={p.userList}
        invoiceContacts={p.invoiceContacts}
        noteTemplates={p.noteTemplates}
        onNoteTemplatesChanged={p.fetchNoteTemplates}
        editInvoice={p.editInvoice}
        reviseInvoice={p.revisionInvoice}
        initialError={p.createDialogInitialError}
      />

      <InvoiceContactsDialog
        open={p.contactsDialogOpen}
        onClose={() => p.setContactsDialogOpen(false)}
        invoiceClient={p.invoiceClient}
        companyId={localStorage.getItem('companyId') || ''}
        contacts={p.invoiceContacts}
        onContactsChanged={p.fetchInvoiceContacts}
        showSnackbar={(message, severity) => p.setSnackbar({ open: true, message, severity })}
      />

      <InvoiceDetailDialog
        open={p.detailDialogOpen}
        onClose={p.handleCloseDetail}
        invoice={p.detailInvoice}
        onEdit={(invoice) => { p.handleCloseDetail(); p.handleOpenEdit(invoice); }}
        onRevise={p.handleOpenRevision}
        onRemind={p.handleRemind}
        onSendEmail={p.handleOpenEmail}
        onIssue={(invoice) => p.handleOpenConfirm('issue', invoice)}
        onDelete={(invoice) => p.handleOpenConfirm('delete', invoice)}
        onMarkPaid={(invoice) => p.handleOpenConfirm('paid', invoice)}
        onVoid={(invoice) => p.handleOpenConfirm('void', invoice)}
        onDownloadPdf={p.handleDownloadPdf}
        onPrintShippingLabel={p.handlePrintShippingLabel}
        onDownloadShippingLabel={p.handleDownloadShippingLabel}
        onRefresh={p.handleRefreshInvoice}
        processingIssueNeedsAttention={p.detailInvoice
          ? Boolean(p.manualReviewInvoices[p.detailInvoice._id]) || (p.processingIssueChecks[p.detailInvoice._id] || 0) >= 3
          : false}
      />

      <InvoiceEmailDialog
        open={p.emailDialogOpen}
        onClose={p.handleCloseEmail}
        invoice={p.emailInvoice}
        invoiceClient={p.invoiceClient}
        onSent={p.handleEmailSent}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>

      {/* Post-issue toast (design_handoff_invoice_actions/README.md, desktop
          only — useInvoicesPage falls back to the plain Snackbar above on
          mobile): the one-click Email Invoice/View path right after Issue. */}
      <Snackbar
        open={p.actionToast.open}
        autoHideDuration={8000}
        onClose={p.closeActionToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          role="status"
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            bgcolor: '#2A1E33', color: '#fff', borderRadius: '12px',
            py: 1.25, pl: 2, pr: 1.5, boxShadow: '0 8px 28px rgba(42,30,51,0.22)',
          }}
        >
          <CheckCircleIcon sx={{ color: '#7ED4A8', fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: '#fff' }}>
            <b>{p.actionToast.invoice?.invoiceNumber}</b> {p.t('issued', { defaultValue: 'issued' })}
            {' · '}
            {formatCurrency(p.actionToast.invoice?.total, p.actionToast.invoice?.currency, i18n.language)}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<MailOutlineIcon sx={{ fontSize: 15 }} />}
            onClick={p.handleActionToastSendEmail}
            sx={{ borderRadius: `${radius.pill}px`, bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6425D0' } }}
          >
            {p.t('Email Invoice', { defaultValue: 'Email Invoice' })}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={p.handleActionToastView}
            sx={{ borderRadius: `${radius.pill}px`, color: '#fff', borderColor: 'rgba(255,255,255,0.28)' }}
          >
            {p.t('View')}
          </Button>
          <IconButton size="small" onClick={p.closeActionToast} aria-label={p.t('Close', { defaultValue: 'Close' })} sx={{ color: '#B9AFC2' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
    </Box>
  );
};

export default Invoices;
